import { gql } from "@apollo/client";
import { Classes, Intent, TooltipProps } from "@blueprintjs/core";
import React, { useCallback, useMemo } from "react";
import styled from "styled-components";

import { HexButton, HexTooltip, Txt } from "../../hex-components";
import { useOnClickProps } from "../../hooks/useOnClickProps";
import { ORG_ID } from "../../orgs";
import { Routes } from "../../route/routes";
import { Heading } from "../Heading";

import {
  WorkspaceAdminContactInfoFragment,
  useWorkspaceAdminsQuery,
} from "./ContactAnAdmin.generated";

const ContactText = styled.a`
  text-decoration: underline;
`;

const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 3px;
`;

const AdminList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AdminEmail = styled.div`
  color: ${({ theme }) => theme.fontColor.MUTED};
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px 3px 1px;

  border-top: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
`;

gql`
  fragment WorkspaceAdminContactInfo on User {
    id
    name
    email
  }
  query WorkspaceAdmins($orgId: OrgId!) {
    orgById(orgId: $orgId) {
      id
      workspaceAdmins {
        id
        ...WorkspaceAdminContactInfo
      }
    }
  }
`;

const stopPropagation: React.MouseEventHandler = (e) => {
  e.stopPropagation();
};
interface AdminInfoRowProps {
  email: WorkspaceAdminContactInfoFragment["email"];
  name: WorkspaceAdminContactInfoFragment["name"];
}

const AndminInfoRow = React.memo<AdminInfoRowProps>(function AdminInfoRow({
  email,
  name,
}) {
  return (
    <div>
      <div className={Classes.TEXT_OVERFLOW_ELLIPSIS}>{name}</div>
      <AdminEmail className={Classes.TEXT_OVERFLOW_ELLIPSIS}>
        {email}
      </AdminEmail>
    </div>
  );
});

export interface ContactAnAdminTooltipProps {
  readonly text?: string;
  usePortal?: boolean;
  position?: TooltipProps["position"];
  children: JSX.Element;
  shouldStopPropagation?: boolean;
  additionalContent?: JSX.Element;
  className?: string;
}

export const ContactAnAdminTooltip = React.memo<ContactAnAdminTooltipProps>(
  function ContactAnAdminTooltip({
    additionalContent,
    children,
    className,
    position,
    shouldStopPropagation = false,
    usePortal = true,
  }) {
    const askYourAdminClickProps = useOnClickProps({
      to: Routes.SETTINGS.getUrl({ subView: "users" }),
      target: "_blank",
      onClick: shouldStopPropagation ? stopPropagation : undefined,
    });

    const {
      data: adminData,
      error,
      loading,
    } = useWorkspaceAdminsQuery({ variables: { orgId: ORG_ID } });

    const adminListMaxInlineCount = 3;

    const workspaceAdminsCount = useMemo(
      () => adminData?.orgById.workspaceAdmins.length ?? 0,
      [adminData],
    );

    const workspaceAdmins = useMemo(
      () =>
        (adminData?.orgById.workspaceAdmins ?? []).slice(
          0,
          adminListMaxInlineCount,
        ),
      [adminData],
    );

    return (
      <HexTooltip
        className={className}
        content={
          <TooltipContent>
            {additionalContent && (
              <>
                {additionalContent}
                <Divider />
              </>
            )}
            <Heading renderAs="h5">Workspace Admins</Heading>
            <AdminList>
              {workspaceAdmins.map((item) => (
                <AndminInfoRow
                  key={item.id}
                  email={item.email}
                  name={item.name}
                />
              ))}
            </AdminList>
            {workspaceAdminsCount > adminListMaxInlineCount && (
              <>
                <Txt>
                  And {workspaceAdminsCount - adminListMaxInlineCount} more...
                </Txt>
                <Divider>
                  <a {...askYourAdminClickProps}>View all users</a>
                </Divider>
              </>
            )}
          </TooltipContent>
        }
        css={`
          padding: 0;
        `}
        disabled={error != null || loading}
        interactionKind="hover"
        position={position}
        scrollContent={true}
        usePortal={usePortal}
      >
        {children}
      </HexTooltip>
    );
  },
);

export type ContactAnAdminProps = {
  readonly text?: string;
  usePortal?: boolean;
  position?: TooltipProps["position"];
  shouldStopPropagation?: boolean | undefined;
  button?: "large" | "default" | "small" | null;
  className?: string;
};

export const ContactAnAdmin: React.FunctionComponent<ContactAnAdminProps> = ({
  button,
  className,
  position,
  shouldStopPropagation = false,
  text = "Contact an Admin",
  usePortal = true,
}) => {
  const handleTooltipTargetOnClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  return (
    <ContactAnAdminTooltip
      className={className}
      position={position}
      shouldStopPropagation={shouldStopPropagation}
      usePortal={usePortal}
    >
      {button ? (
        <HexButton
          intent={Intent.PRIMARY}
          large={button === "large"}
          small={button === "small"}
          onClick={handleTooltipTargetOnClick}
        >
          {text}
        </HexButton>
      ) : (
        <ContactText onClick={handleTooltipTargetOnClick}>{text}</ContactText>
      )}
    </ContactAnAdminTooltip>
  );
};
